import React, { useEffect, useState } from "react";
import { Table, Input, Tag, Button, message } from "antd";
import {
  FileTextOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { sendGet } from "../../../api/axios";
import { linkApi } from "../../../common/ngok";
import { showConfirm } from "../../../helpers/modal-confirm";
import ModalAdd from "./ModalAdd/ModalAdd";
import moment from "moment";
import styled from "styled-components";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  overflow: hidden;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  flex-shrink: 0;
`;

const ContentRow = styled.div`
  flex: 1;
  display: flex;
  gap: 20px;
  min-height: 0;
  overflow: hidden;
`;

const TablePanel = styled.div`
  flex: 1;
  overflow-y: auto;
  min-width: 0;
`;

const PdfPanel = styled.div`
  width: 45%;
  min-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
`;

const PdfPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 16px;
  color: #888;
  text-align: center;
  padding: 24px;
`;

const { Search } = Input;

const ListHospital = () => {
  const { t } = useTranslation();
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const token = useSelector((state) => state?.auth?.token);

  const getDataAwarded = async (callback) => {
    setLoading(true);
    try {
      const response = await sendGet("/dm/data/hospital?info=awarded");
      if (response?.status === "successful") {
        setHospitals(response?.hospitals || []);
        setIsOpen(false);
        if (callback) callback(response?.hospitals);
      }
    } catch (e) {
      message.error(t("document.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDataAwarded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpdate = async (year, code) => {
    const myHeaders = new Headers({
      Authorization: "Token " + token,
      "Content-Type": "application/x-www-form-urlencoded",
    });
    try {
      const response = await fetch(`${linkApi}/dm/data/hospital?code=${code}`, {
        method: "POST",
        headers: myHeaders,
        body: new URLSearchParams({
          last_awarded_year:
            year !== "None" ? moment(year).format("YYYY") : year,
        }),
      });
      const data = await response.json();
      if (year !== "None") {
        setHospitals(
          hospitals.map((el) =>
            data?.hospital?.code === el.code ? data.hospital : el
          )
        );
      } else {
        setHospitals(hospitals.filter((el) => code !== el.code));
        setSelected(null);
      }
      message.success(t("document.updateSuccess"));
    } catch {
      message.error(t("document.updateError"));
    }
  };

  const onDelete = (code, name) => {
    showConfirm({
      title: `${t("document.confirmDeleteHospital")} "${name}"?`,
      onOk: () => onUpdate("None", code),
    });
  };

  const years = [
    ...new Set(hospitals.map((h) => h.last_awarded_year).filter(Boolean)),
  ].sort();

  const filtered = hospitals.filter((h) => {
    const matchSearch = h.name
      ?.toLowerCase()
      .includes(search.toLowerCase());
    return matchSearch;
  });

  const columns = [
    {
      title: t("document.hospitalName"),
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (val, record) => (
        <span
          style={{
            cursor: "pointer",
            color: selected?.code === record.code ? "#0089CF" : "inherit",
            fontWeight: selected?.code === record.code ? 600 : 400,
          }}
          onClick={() => setSelected(record)}
        >
          {val}
        </span>
      ),
    },
    {
      title: t("document.awardedYear"),
      dataIndex: "last_awarded_year",
      key: "last_awarded_year",
      width: 130,
      sorter: (a, b) =>
        (a.last_awarded_year || "").localeCompare(b.last_awarded_year || ""),
      filters: years.map((y) => ({ text: y, value: y })),
      onFilter: (value, record) => record.last_awarded_year === value,
      render: (val) =>
        val ? <Tag color="blue">{val}</Tag> : <Tag>-</Tag>,
    },
    {
      title: t("document.certificate"),
      key: "cert",
      width: 100,
      align: "center",
      render: (_, record) =>
        record.last_coe_cert && record.last_coe_cert !== "None" ? (
          <FileTextOutlined
            style={{ fontSize: 18, color: "#52c41a", cursor: "pointer" }}
            onClick={() => setSelected(record)}
          />
        ) : (
          <span style={{ color: "#ccc" }}>-</span>
        ),
    },
    {
      title: t("userManagement.action"),
      key: "action",
      width: 70,
      align: "center",
      render: (_, record) => (
        <DeleteOutlined
          style={{ color: "#ff4d4f", cursor: "pointer" }}
          onClick={() => onDelete(record.code, record.name)}
        />
      ),
    },
  ];

  return (
    <Wrapper>
      <ToolbarRow>
        <Search
          placeholder={t("document.searchHospital")}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsOpen(true)}
        >
          {t("document.addHospital")}
        </Button>
        <span style={{ color: "#888", marginLeft: "auto" }}>
          {t("document.totalAwarded")}: {hospitals.length}
        </span>
      </ToolbarRow>

      <ContentRow>
        <TablePanel>
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="code"
            size="small"
            loading={loading}
            pagination={{
              defaultPageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: ["10", "15", "30", "50"],
            }}
            onRow={(record) => ({
              onClick: () => setSelected(record),
              style: {
                cursor: "pointer",
                background:
                  selected?.code === record.code ? "#e6f7ff" : "inherit",
              },
            })}
          />
        </TablePanel>

        <PdfPanel>
          {selected &&
          selected.last_coe_cert &&
          selected.last_coe_cert !== "None" ? (
            <iframe
              key={selected.code}
              title="COE Certificate"
              src={`${linkApi}/media/${selected.last_coe_cert}`}
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : selected ? (
            <PdfPlaceholder>{t("common.updateDocument")}</PdfPlaceholder>
          ) : (
            <PdfPlaceholder>{t("document.selectToView")}</PdfPlaceholder>
          )}
        </PdfPanel>
      </ContentRow>

      <ModalAdd
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        getDataAwarded={getDataAwarded}
        setSelected={setSelected}
      />
    </Wrapper>
  );
};

export default ListHospital;
